import SettingsTabBase from "./SettingsTabBase.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { assignViewsState } from "../../../other/utils/otherUtils.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { cellMenuItemRemoveRow } from "../../../other/utils/gridCellMenuUtils.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import EditorBase from "../base/EditorBase.jsx";

export default class SwivuSettingsIncentiveTagsContainer extends SettingsTabBase {
  static get $name() {
    return "SwivuSettingsIncentiveTagsContainer";
  }

  static get type() {
    return "swivusettingsincentivetags";
  }

  get swivuStore() {
    return this.project.getCrudStore(STORE_KEYS.incentiveTagsStore);
  }

  static get configurable() {
    return {
      title: "Points Tags",
      items: [
        {
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          ref: "tagsGrid",
          cls: "!p-0 w-full",
          height: "100%",
          emptyText: "No tags created yet.",
          features: {
            sort: false,
            group: false,
            cellMenu: {
              items: {
                cut: false,
                copy: false,
                paste: false,
                removeRow: { ...cellMenuItemRemoveRow(), onItem: "up.onRemoveTag" }
              }
            }
          },
          listeners: {
            cellClick: "up.onGridCellClick"
          },
          columns: [
            {
              id: "name",
              field: "name",
              text: "Tag",
              editor: {
                required: true,
                placeholder: "Enter Tag"
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              flex: 1
            },
            {
              id: "description",
              field: "description",
              text: "Description",
              editor: {
                placeholder: "Enter Description"
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              flex: 1
            },
            {
              id: "categoryId",
              text: "Category",
              type: WIDGET_KEYS.incentiveCategoryColumn,
              flex: 1
            }
          ],
          tbar: {
            cls: "swivu-modern-grid-tbar [&_.b-toolbar-content]:items-start [&_.b-toolbar-content]:p-0 [&_.b-toolbar-content]:!min-h-[auto] mb-swivu-base bg-transparent [&_.b-toolbar-content]:!flex-col",
            items: {
              titleContainer: {
                type: "container",
                cls: "flex-1 flex flex-col gap-1",
                items: {
                  title: {
                    type: "displayField",
                    value: "Tags",
                    cls: "swivu-grid-title text-swivu-md font-semibold text-[#282828] dark:text-[#f0f0f0] [&_span]:!p-0"
                  },
                  subtitle: {
                    type: "displayField",
                    value: "Create tags within categories for creating incentives. Categories must be created first before adding tags.",
                    cls: "text-swivu-sm text-muted-foreground [&_span]:!p-0"
                  },
                }
              },
            },
          },
          bbar: {
            cls: "justify-end !px-swivu-base",
            items: {
              addButton: {
                type: "button",
                icon: "b-fa b-fa-plus",
                color: "b-green",
                onClick: "up.onAddTag"
              },
              deleteButton: {
                type: "button",
                icon: "b-fa b-fa-trash",
                color: "b-red",
                onClick: "up.onRemoveTag"
              }
            }
          }
        }
      ]
    };
  }

  onAddTag() {
    const { tagsGrid } = this.widgetMap;
    const newRecord = this.swivuStore.createRecord({
      name: "",
      categoryId: null
    });
    newRecord.project = this.project;
    tagsGrid.store.add(newRecord);
    tagsGrid.startEditing({ record: newRecord, field: "name" });
  }

  onRemoveTag() {
    const { tagsGrid } = this.widgetMap;
    const removeSelected = tagsGrid.selectedRecords;
    if (removeSelected.length) {
      const selectedIds = removeSelected.map((r) => r.id);
      tagsGrid.store.remove(selectedIds);
      this.swivuStore.remove(selectedIds);
    }
  }

  attachState(gridWidget, widgetRef) {
    gridWidget.stateId = `b-settings-tab-${widgetRef}`;
    assignViewsState([gridWidget], this.stateProvider);
  }

  loadRecord(record, context, otherParams) {
    if (!otherParams.isFirstShow) return;
    super.loadRecord(record, context);
    const { tagsGrid } = this.widgetMap;
    tagsGrid.store.data = this.swivuStore.query(() => true, true);
    this.attachState(tagsGrid, PERSISTENCE_STATE_KEYS.boardSettingIncentiveTagsGrid);
    tagsGrid.columns.getById("categoryId").onLoad(context.project, { ...context, swivuTab: this });

    this.updateUIBasedOnCategories();

    const detacher = this.project.incentiveTagCategoryStore.on("change", () => {
      this.updateUIBasedOnCategories();
    });

    this.up(EditorBase.type, true).on("hide", () => {
      detacher();
    })
  }

  onGridCellClick({ record, column, event }) {
    if (column.id === "categoryId") {
      this.editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
        entityRecord: record,
        column,
        event,
      }, { by: "recordType", useExisting: true });
    }
  }

  swivuCheckGrants(user) {
    if (!user.can(GRANTS.grant_one)) {
      const {tagsGrid: grid, addButton, deleteButton}= this.widgetMap;
      grid.disabled = true;
      grid.readOnly = true;
      // Disable action buttons in toolbar
      addButton.disabled = true;
      deleteButton.disabled = true;
    }
  }

  validateTagField(field, value) {
    switch (field) {
      case "name": {
        const name = value.trim();
        if (!name) return "Name is required";
        return true;
      }
      default:
        return true;
    }
  }

  isTagRecordComplete(record) {
    const hasName = record.name.trim().length > 0;
    const hasCategory = Boolean(record.categoryId);
    return hasName && hasCategory;
  }

  onFinalizeCellEdit({ value, record, editorContext }) {
    const { column } = editorContext;
    const validation = this.validateTagField(column.field, value, record);
    if (validation !== true) return validation;
    this.handleRecordUpdate(column.field, value, record);
    return true;
  }

  handleRecordUpdate(field, value, record) {
    record[field] = value;
    if (record.isPhantom && this.isTagRecordComplete(record)) {
      this.swivuStore.add(record);
    }
  }

  hasCategories() {
    return this.project.getCrudStore(STORE_KEYS.incentiveTagCategoryStore).query(() => true, true).length > 0;
  }

  updateUIBasedOnCategories() {
    const { addButton, deleteButton } = this.widgetMap;
    const hasCategories = this.hasCategories();
    addButton.disabled = !hasCategories;
    deleteButton.disabled = !hasCategories;
    addButton.tooltip = !hasCategories ? "Create a category first" : "";
  }

}

SwivuSettingsIncentiveTagsContainer.initClass();
